import { memo } from 'react';
import { Line } from '@react-three/drei';

/**
 * 3D-native version of the in-progress zone-polygon preview. Same
 * visual idea as `ZonePolygonOverlay` (2D): small dots at each placed
 * corner, an open polyline through them, a dashed guide line from the
 * last corner to the cursor, and a dashed close-hint back to the first
 * corner once ≥2 corners exist.
 *
 * Renders directly inside the R3F scene so the preview tracks the
 * pitch plane under any camera preset.
 */

const OVERLAY_Y_M = 0.013;

export interface ZonePolygonOverlay3DProps {
  corners: ReadonlyArray<{ x: number; z: number }>;
  hover: { x: number; z: number } | null;
  stroke: string;
  strokeWidth: number;
}

function pt3(p: { x: number; z: number }): [number, number, number] {
  return [p.x, OVERLAY_Y_M, p.z];
}

function ZonePolygonOverlay3DInner({
  corners,
  hover,
  stroke,
  strokeWidth,
}: ZonePolygonOverlay3DProps) {
  if (corners.length === 0 && hover === null) return null;

  const polyline = corners.length >= 2 ? corners.map(pt3) : null;
  const last = corners.length > 0 ? corners[corners.length - 1]! : null;
  const first = corners.length >= 2 ? corners[0]! : null;

  // Corner dot radius — scaled off stroke so dense/loose lines both
  // produce a readable target marker.
  const dotR = Math.max(0.25, strokeWidth * 1.4);

  return (
    <group raycast={() => undefined} renderOrder={5}>
      {polyline && (
        <Line
          points={polyline}
          color={stroke}
          lineWidth={strokeWidth * 12}
          transparent
          depthWrite={false}
        />
      )}
      {corners.map((p, i) => (
        <mesh key={i} position={[p.x, OVERLAY_Y_M, p.z]}>
          <sphereGeometry args={[dotR, 10, 10]} />
          <meshBasicMaterial
            color={stroke}
            transparent
            depthWrite={false}
          />
        </mesh>
      ))}
      {last && hover && (
        <Line
          points={[pt3(last), pt3(hover)]}
          color={stroke}
          lineWidth={strokeWidth * 12}
          dashed
          dashSize={strokeWidth * 4}
          gapSize={strokeWidth * 2.5}
          transparent
          depthWrite={false}
        />
      )}
      {first && hover && (
        <Line
          points={[pt3(hover), pt3(first)]}
          color={stroke}
          lineWidth={strokeWidth * 7}
          dashed
          dashSize={strokeWidth * 4}
          gapSize={strokeWidth * 2.5}
          transparent
          opacity={0.6}
          depthWrite={false}
        />
      )}
    </group>
  );
}

export const ZonePolygonOverlay3D = memo(ZonePolygonOverlay3DInner);
